<template>
  <Modal position="end" ref="modalRef">
    <template #header>Lockbox {{ serialNumber }}</template>

    <template #body>
      <View direction="column" gap="lg">
        <Text variant="body-medium" color="secondary">
          Every code this lockbox has issued, straight from the CodeBox portal.
        </Text>

        <View v-if="query.isPending.value" justify="center" padding="xl">
          <Spinner size="medium" />
        </View>

        <Text v-else-if="query.error.value" variant="body-medium" color="secondary">
          Could not load the history for this lockbox. Check the CodeBox connection and try again.
        </Text>

        <Text v-else-if="!entries.length" variant="body-medium" color="secondary">
          No codes have been issued for this lockbox yet.
        </Text>

        <View v-else direction="column" gap="sm" data-testid="codebox-history-entries">
          <Card v-for="(entry, index) in entries" :key="`${entry.date}-${index}`" size="small">
            <View direction="column" gap="xs">
              <View gap="sm" align="center" justify="space-between">
                <Text variant="label-medium">{{ formatDate(entry.date) }}</Text>
                <Badge variant="grey" :label="entry.action" size="small" />
              </View>

              <Text v-if="entry.name" variant="body-medium" color="secondary">{{ entry.name }}</Text>
              <Text v-if="contactOf(entry)" variant="body-small" color="tertiary">{{ contactOf(entry) }}</Text>
            </View>
          </Card>
        </View>
      </View>
    </template>

    <template #footer>
      <Button variant="ghost" @click="close">Close</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { format, isValid, parseISO } from 'date-fns';
import { computed, ref } from 'vue';

import { Badge, Button, Card, Modal, Spinner, Text, View } from '@tallo/design-system';
import { CodeboxHistoryEntryDto, useCodeboxHistoryQuery } from '@tallo/investor/property';

const props = defineProps<{ codeboxId: string | null; serialNumber: string | null }>();

const modalRef = ref<InstanceType<typeof Modal> | null>(null);

const query = useCodeboxHistoryQuery(() => props.codeboxId);

// The vendor does not guarantee an order, so the log is sorted rather than reversed.
const entries = computed(() =>
  [...(query.data.value?.history ?? [])].sort(
    (a, b) => (parseEntryDate(b)?.getTime() ?? 0) - (parseEntryDate(a)?.getTime() ?? 0),
  ),
);

function parseEntryDate(entry: CodeboxHistoryEntryDto): Date | null {
  return toDate(entry.date);
}

/** CodeBox sends US-style dates (02/02/2026), which parseISO cannot read. */
function toDate(value: string): Date | null {
  const iso = parseISO(value);
  if (isValid(iso)) return iso;

  const loose = new Date(value);
  return isValid(loose) ? loose : null;
}

function contactOf(entry: CodeboxHistoryEntryDto): string {
  return [entry.email, entry.mobilePhone ?? entry.officePhone].filter(Boolean).join(' · ');
}

function formatDate(value: string): string {
  const parsed = toDate(value);
  if (!parsed) return value;

  // Midnight means the vendor gave a date with no time, so a "12:00 AM" would be invented precision.
  const hasTime = parsed.getHours() !== 0 || parsed.getMinutes() !== 0;

  return format(parsed, hasTime ? 'MMM d, yyyy · h:mm a' : 'MMM d, yyyy');
}

function open() {
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

defineExpose({ open, close });
</script>
